import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Plus, Trash2 } from "lucide-react";

export type DamageMarker = {
  id?: string;
  x_percent: number;
  y_percent: number;
  damage_type: string;
  severity: "Minor" | "Moderate" | "Severe";
  notes: string;
};

type Props = {
  imageUrl: string;
  photoType: string;
  markers: DamageMarker[];
  onMarkersChange: (markers: DamageMarker[]) => void;
  readOnly?: boolean;
};

const DAMAGE_TYPES = [
  "Scratch",
  "Dent",
  "Crack",
  "Chip",
  "Rust",
  "Paint Damage",
  "Broken Part",
  "Missing Part",
  "Stain",
  "Tear",
  "Other",
];

const SEVERITY_COLORS: Record<string, string> = {
  Minor: "#22c55e",
  Moderate: "#f59e0b",
  Severe: "#ef4444",
};

const DamageMarkerOverlay: React.FC<Props> = ({
  imageUrl,
  photoType,
  markers,
  onMarkersChange,
  readOnly = false,
}) => {
  const [isAddingMarker, setIsAddingMarker] = useState(false);
  const [pendingMarker, setPendingMarker] = useState<Partial<DamageMarker> | null>(null);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<number | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (readOnly || !isAddingMarker) return;

    const container = imageContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x_percent = ((e.clientX - rect.left) / rect.width) * 100;
    const y_percent = ((e.clientY - rect.top) / rect.height) * 100;

    setPendingMarker({
      x_percent,
      y_percent,
      damage_type: "",
      severity: "Minor",
      notes: "",
    });
    setIsAddingMarker(false);
  };

  const handleSaveMarker = () => {
    if (!pendingMarker || !pendingMarker.damage_type) return;

    const newMarker: DamageMarker = {
      x_percent: pendingMarker.x_percent!,
      y_percent: pendingMarker.y_percent!,
      damage_type: pendingMarker.damage_type,
      severity: pendingMarker.severity as "Minor" | "Moderate" | "Severe",
      notes: pendingMarker.notes || "",
    };

    onMarkersChange([...markers, newMarker]);
    setPendingMarker(null);
  };

  const handleDeleteMarker = (index: number) => {
    const newMarkers = markers.filter((_, i) => i !== index);
    onMarkersChange(newMarkers);
    setSelectedMarkerIndex(null);
  };

  const handleMarkerClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedMarkerIndex(selectedMarkerIndex === index ? null : index);
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-200">
          {photoType.replace(/_/g, " ").toUpperCase()}
          {markers.length > 0 && (
            <span className="ml-2 text-xs text-amber-500">
              ({markers.length} damage marker{markers.length !== 1 ? "s" : ""})
            </span>
          )}
        </div>
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            variant={isAddingMarker ? "secondary" : "outline"}
            onClick={() => {
              setIsAddingMarker(!isAddingMarker);
              setPendingMarker(null);
              setSelectedMarkerIndex(null);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            {isAddingMarker ? "Cancel" : "Add Damage"}
          </Button>
        )}
      </div>

      {/* Instructions */}
      {isAddingMarker && !pendingMarker && (
        <div className="text-sm text-amber-500 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
          Click on the image to mark the damage location
        </div>
      )}

      {/* Image with markers */}
      <div
        ref={imageContainerRef}
        className={`relative rounded-lg overflow-hidden border border-gray-600 bg-white ${
          isAddingMarker ? "cursor-crosshair" : "cursor-default"
        }`}
        onClick={handleImageClick}
      >
        <img
          src={imageUrl}
          alt={photoType}
          className="w-full h-auto"
          draggable={false}
        />

        {/* Existing markers */}
        {markers.map((marker, index) => (
          <div
            key={index}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-10"
            style={{
              left: `${marker.x_percent}%`,
              top: `${marker.y_percent}%`,
            }}
            onClick={(e) => handleMarkerClick(index, e)}
          >
            {/* Marker pin */}
            <div
              className={`w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold shadow-lg transition-transform ${
                selectedMarkerIndex === index ? "scale-125" : "hover:scale-110"
              }`}
              style={{ backgroundColor: SEVERITY_COLORS[marker.severity] }}
            >
              {index + 1}
            </div>

            {/* Marker tooltip on selection */}
            {selectedMarkerIndex === index && (
              <div
                className="absolute z-20 bg-gray-900 border border-gray-600 rounded-lg p-3 shadow-xl min-w-[200px]"
                style={{
                  left: marker.x_percent > 70 ? "auto" : "100%",
                  right: marker.x_percent > 70 ? "100%" : "auto",
                  top: "50%",
                  transform: "translateY(-50%)",
                  marginLeft: marker.x_percent > 70 ? 0 : "8px",
                  marginRight: marker.x_percent > 70 ? "8px" : 0,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm text-gray-200">
                    {marker.damage_type}
                  </span>
                  <button
                    className="text-gray-400 hover:text-white"
                    onClick={() => setSelectedMarkerIndex(null)}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div
                  className="text-xs px-2 py-0.5 rounded inline-block mb-2"
                  style={{
                    backgroundColor: `${SEVERITY_COLORS[marker.severity]}20`,
                    color: SEVERITY_COLORS[marker.severity],
                  }}
                >
                  {marker.severity}
                </div>
                {marker.notes && (
                  <p className="text-xs text-gray-400 mt-1">{marker.notes}</p>
                )}
                {!readOnly && (
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="w-full mt-2"
                    onClick={() => handleDeleteMarker(index)}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Pending marker */}
        {pendingMarker && (
          <div
            className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
            style={{
              left: `${pendingMarker.x_percent}%`,
              top: `${pendingMarker.y_percent}%`,
            }}
          >
            <div className="w-6 h-6 rounded-full border-2 border-white bg-blue-500 flex items-center justify-center text-white text-xs font-bold shadow-lg animate-pulse">
              ?
            </div>
          </div>
        )}
      </div>

      {/* Pending marker form */}
      {pendingMarker && (
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4 space-y-3">
          <div className="text-sm font-semibold text-amber-500">
            Describe the Damage
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Damage Type *</Label>
              <Select
                value={pendingMarker.damage_type || ""}
                onValueChange={(v) =>
                  setPendingMarker((p) => ({ ...p, damage_type: v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {DAMAGE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Severity *</Label>
              <Select
                value={pendingMarker.severity || "Minor"}
                onValueChange={(v) =>
                  setPendingMarker((p) => ({
                    ...p,
                    severity: v as "Minor" | "Moderate" | "Severe",
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Minor">
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: SEVERITY_COLORS.Minor }}
                      />
                      Minor
                    </span>
                  </SelectItem>
                  <SelectItem value="Moderate">
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: SEVERITY_COLORS.Moderate }}
                      />
                      Moderate
                    </span>
                  </SelectItem>
                  <SelectItem value="Severe">
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: SEVERITY_COLORS.Severe }}
                      />
                      Severe
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              value={pendingMarker.notes || ""}
              onChange={(e) =>
                setPendingMarker((p) => ({ ...p, notes: e.target.value }))
              }
              placeholder="Additional details about the damage..."
              rows={2}
            />
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleSaveMarker}
              disabled={!pendingMarker.damage_type}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              Save Marker
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingMarker(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Markers summary list */}
      {markers.length > 0 && !pendingMarker && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-400 uppercase">
            Damage Summary
          </div>
          <div className="space-y-1">
            {markers.map((marker, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-colors ${
                  selectedMarkerIndex === index
                    ? "bg-gray-700/50 border-amber-500/50"
                    : "bg-gray-800/30 border-gray-700 hover:bg-gray-700/30"
                }`}
                onClick={() =>
                  setSelectedMarkerIndex(selectedMarkerIndex === index ? null : index)
                }
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: SEVERITY_COLORS[marker.severity] }}
                  >
                    {index + 1}
                  </div>
                  <span className="text-sm text-gray-200">{marker.damage_type}</span>
                  <span
                    className="text-xs px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: `${SEVERITY_COLORS[marker.severity]}20`,
                      color: SEVERITY_COLORS[marker.severity],
                    }}
                  >
                    {marker.severity}
                  </span>
                </div>
                {!readOnly && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-400/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteMarker(index);
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DamageMarkerOverlay;
